import React, { createContext, useCallback, useEffect, useState } from "react";
import {
  buildSubject,
  clearCachedCapabilities,
  fetchCapabilities,
  readCachedCapabilities,
} from "../services/capabilitiesService";
import {
  getAccessTokenExpiry,
  refreshAccessToken,
  TOKEN_REFRESHED_EVENT,
} from "../services/sessionRefresh";

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [uiConfig, setUiConfig] = useState(null);
  const [forceLogout, setForceLogout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uiConfigLoading, setUiConfigLoading] = useState(true);

  const applyCapabilities = useCallback((capabilities) => {
    if (!capabilities) {
      setUser(null);
      setUiConfig(null);
      return;
    }
    setUser(buildSubject(capabilities));
    setUiConfig(capabilities.uiConfig || null);
  }, []);

  const refreshCapabilities = useCallback(async () => {
    setUiConfigLoading(true);
    try {
      const capabilities = await fetchCapabilities();
      applyCapabilities(capabilities);
    } catch {
      // Fall back to cached snapshot when the network call fails (initial offline / transient).
      const cached = readCachedCapabilities();
      if (cached) applyCapabilities(cached);
    } finally {
      setUiConfigLoading(false);
    }
  }, [applyCapabilities]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");

    // Show the cached snapshot immediately so an intermittent user doesn't get
    // a flash of empty UI while we re-validate in the background.
    const cached = readCachedCapabilities();
    if (cached) applyCapabilities(cached);

    if (token || refreshToken) {
      // Don't judge the access token's expiry here. Access tokens only live 1h,
      // so reloading the app after an hour of intermittent use would otherwise
      // force a logout even though the 7-day refresh token is still valid.
      // Instead, always re-fetch capabilities through the axios interceptor: it
      // transparently refreshes an expired access token on a 401 and only fires
      // forceLogout when the refresh token itself is gone or expired (i.e. the
      // session is genuinely over).
      refreshCapabilities();
    } else {
      // No credentials at all — unauthenticated visitor, route to /login.
      setUiConfigLoading(false);
    }

    setLoading(false);
  }, [applyCapabilities, refreshCapabilities]);

  useEffect(() => {
    const handleForcedLogout = () => setForceLogout(true);
    window.addEventListener("forceLogout", handleForcedLogout);
    return () => window.removeEventListener("forceLogout", handleForcedLogout);
  }, []);

  // Proactive "sliding session" refresh (Google/Microsoft-style): keep a valid
  // access token while the user is active so the reactive 401 path — and its
  // user-visible failure window — rarely fires, and so the rolling 7-day
  // refresh token keeps renewing across days of continuous use.
  const authed = !!user;
  useEffect(() => {
    if (!authed) return undefined;

    let timer = null;
    const SKEW_MS = 60 * 1000; // refresh ~1 min before the token actually expires
    const MAX_DELAY_MS = 55 * 60 * 1000; // access tokens live 1h; never park longer
    const hasSession = () => !!localStorage.getItem("refreshToken");

    const clear = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = () => {
      clear();
      if (!hasSession()) return;
      const expiry = getAccessTokenExpiry();
      // Fall back to a conservative cadence if the expiry can't be read.
      let ms = expiry ? expiry - Date.now() - SKEW_MS : 50 * 60 * 1000;
      ms = Math.max(0, Math.min(ms, MAX_DELAY_MS));
      timer = setTimeout(runRefresh, ms);
    };

    const runRefresh = async () => {
      if (!hasSession()) return;
      try {
        await refreshAccessToken();
        // On success the TOKEN_REFRESHED_EVENT handler reschedules.
      } catch {
        // Fatal already dispatched forceLogout; transient keeps the session —
        // reschedule so we retry rather than sit dead.
        schedule();
      }
    };

    // On tab focus / reconnect (e.g. laptop resumed from sleep where the timer
    // never fired), refresh immediately if the token is expired or about to be.
    const onWake = () => {
      if (document.visibilityState !== "visible" || !hasSession()) return;
      const expiry = getAccessTokenExpiry();
      if (!expiry || expiry - Date.now() < 90 * 1000) runRefresh();
      else schedule();
    };

    const onRefreshed = () => schedule();

    schedule();
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("online", onWake);
    window.addEventListener(TOKEN_REFRESHED_EVENT, onRefreshed);

    return () => {
      clear();
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("online", onWake);
      window.removeEventListener(TOKEN_REFRESHED_EVENT, onRefreshed);
    };
  }, [authed]);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("role");
    localStorage.removeItem("roles");
    clearCachedCapabilities();
    setUser(null);
    setUiConfig(null);
    setForceLogout(false);
    setUiConfigLoading(false);
  };

  const value = {
    user,
    setUser,
    uiConfig,
    setUiConfig,
    refreshUiConfig: refreshCapabilities,
    refreshCapabilities,
    logout,
    forceLogout,
    setForceLogout,
    loading,
    uiConfigLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
